import { memo, useCallback, useMemo, useState } from "react";
import { cn, formatCurrency } from "@/lib/utils";
import { Spinner } from "@/components/spinner";
import { useI18n } from "@/features/i18n";

export type Side = "buy" | "sell";

type Asset = {
  code: string;
  precision: number;
};

export type MarketTrade = {
  id: string;
  price: number;
  quantity: number;
  time: number;
  side: Side;
};

type MarketTradesProps = {
  base: Asset;
  quote: Asset;
  trades: MarketTrade[];
  isLoading?: boolean;
};

const ROW_HEIGHT = 24; // Height of each trade row in pixels
const HEADER_HEIGHT = 28; // Height of header row

// Memoized trade row to prevent unnecessary re-renders
// Only re-renders when the specific trade data changes
type TradeRowProps = {
  trade: MarketTrade;
  quotePrecision: number;
  basePrecision: number;
};

const TradeRow = memo(function TradeRow({ 
  trade, 
  quotePrecision, 
  basePrecision 
}: TradeRowProps) {
  const { formatTradingTime } = useI18n();
  const color = trade.side === "buy" ? "text-bid-foreground" : "text-ask-foreground";
  
  return (
    <div
      className="grid grid-cols-3 px-2 py-1 text-xs gap-2"
      style={{ height: ROW_HEIGHT }}
    >
      <div className={cn(color, "truncate flex items-center")}>
        {formatCurrency(trade.price, quotePrecision)}
      </div>
      <div className="text-right truncate flex items-center justify-end">
        {formatCurrency(trade.quantity, basePrecision)}
      </div>
      <div className="text-right truncate flex items-center justify-end text-muted-foreground">
        {formatTradingTime(trade.time)}
      </div>
    </div>
  );
});

export const MarketTrades = ({
  base,
  quote,
  trades,
  isLoading,
}: MarketTradesProps) => {
  const [containerHeight, setContainerHeight] = useState(0);

  // Calculate how many rows fit in the container
  const visibleRowCount = useMemo(() => {
    if (containerHeight === 0) return 20; // Default
    const availableHeight = Math.max(containerHeight - HEADER_HEIGHT, 0);
    return Math.max(Math.floor(availableHeight / ROW_HEIGHT), 10);
  }, [containerHeight]);

  // Slice trades to fill the visible area
  const visibleTrades = useMemo(() => {
    return trades.slice(0, visibleRowCount);
  }, [trades, visibleRowCount]);

  // Measure container height
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) return;

    const updateHeight = () => {
      setContainerHeight(node.clientHeight);
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(node);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div ref={containerRef} className="flex h-full flex-col">
      <div className="grid grid-cols-3 px-2 py-1 text-xs text-muted-foreground flex-shrink-0">
        <div>Price ({quote.code})</div>
        <div className="text-right">Amount ({base.code})</div>
        <div className="text-right">Time</div>
      </div>

      {isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <div className="flex-1 overflow-hidden">
          {visibleTrades.map((trade) => (
            <TradeRow
              key={trade.id}
              trade={trade}
              quotePrecision={quote.precision}
              basePrecision={base.precision}
            />
          ))}
        </div>
      )}
    </div>
  );
};
